import React from 'react';

import { Callout } from './shared';

const DashboardOverview: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-4">Dashboard Overview</h2>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      The dashboard is the home screen of MFlow. It shows aggregated metrics across all your synced projects for the
      selected date range.
    </p>

    <Callout type="info">
      The KPI cards below — and everything on the Studio and Projects pages — are computed from <strong>Jira</strong>{' '}
      data only, which is why you&apos;ll see a <span className="badge badge-sm">Jira</span> badge next to the page
      title whenever Jira is connected. Trello boards and Standalone projects don&apos;t have a portfolio-wide view yet
      — they show up in a separate, collapsible <strong>Other projects</strong> section instead; open one directly to
      see its stats.
    </Callout>

    <h3 className="font-semibold mb-2 mt-5">Date range &amp; project filter</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Use the <strong>date picker</strong> and <strong>project selector</strong> in the top bar to scope the data. All
      charts and KPI cards update instantly when you change these filters.
    </p>

    <h3 className="font-semibold mb-2">KPI cards</h3>
    <div className="overflow-x-auto mb-5">
      <table className="table table-sm text-sm">
        <thead>
          <tr>
            <th>Metric</th>
            <th>What it measures</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['PSI', 'Project Stability Index — overall health score (0–100). See PSI Score.'],
            [
              'Throughput',
              'Completed ÷ created tasks in the selected range — how much incoming work actually got finished.',
            ],
            ['Bug ratio', '% of created tasks that are type "Bug". A sub-line under Throughput, not its own card.'],
            ['Reopen rate', '% of completed tasks reopened afterward. Lower is better.'],
            ['Cycle time', 'Average time from a task starting to it being done, shown in days.'],
            [
              'Flow efficiency',
              '% of cycle time actually spent in an active status, vs. waiting. Higher is healthier.',
            ],
          ].map(([m, d]) => (
            <tr key={m}>
              <td className="font-medium whitespace-nowrap">{m}</td>
              <td className="text-base-content/60">{d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout type="info">
      <strong>Delivery accuracy</strong> (% completed on or before their due date) and per-status{' '}
      <strong>bottlenecks</strong> aren&apos;t shown on this portfolio-wide dashboard — they live on each individual
      project&apos;s own stats page instead, alongside a per-status breakdown of where work is actually stuck.
      Standalone projects additionally show a forecast completion date, extrapolated from recent throughput.
    </Callout>

    <h3 className="font-semibold mb-2 mt-5">Navigating projects</h3>
    <p className="text-sm text-base-content/70 leading-relaxed">
      Click any project card on the <strong>Projects</strong> dashboard to drill into that project&apos;s individual
      stats, board, and (for Jira) sprint breakdown and team members.
    </p>
  </div>
);

export default DashboardOverview;
